import ToggleSwitch from '@/components/buttons/ToggleSwitch';
import { SelectField } from '@/components/fields';
import RangeField from '@/components/fields/RangeField';
import { ISelectFieldOption } from '@/components/fields/SelectField';
import { confirmModal } from '@/components/modal/Modal';
import { useKeycloak } from '@/contexts/KeycloakContext';
import { Button } from '@/styles/buttons';
import { Row } from '@/styles/common';
import { ScenarioConfig, ScenarioConfigItem } from '@/styles/scenario';
import axios from '@/utils/axios';
import { buildParams, currentYear, isTupleInRange } from '@/utils/utils';
import { produce } from 'immer';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useTheme } from 'styled-components';
import type {Theme as ThemeType} from '@/styles/Themes'

interface DemandConfigProps {
	setScenarioData: React.Dispatch<React.SetStateAction<TDemandScenarioData[]>>;
	selectedItem: TDemandTreeSelectedItem | undefined;
	scenarioId: TValue | undefined;
	scenarios: ISelectFieldOption[] | undefined;
	setSelectedItem: React.Dispatch<
		React.SetStateAction<TDemandTreeSelectedItem | undefined>
	>;
	min: number;
	max: number;
	yearRange: [number, number];
	setYearRange: React.Dispatch<React.SetStateAction<[number, number]>>;
	currentScenarioRange: [number, number] | null;
	refetchScenario: () => void;
	hasScenarioBeenUpdated: boolean;
}

const DemandConfig: React.FC<DemandConfigProps> = ({
	setScenarioData,
	selectedItem,
	scenarioId,
	scenarios,
	setSelectedItem,
	min,
	max,
	yearRange,
	setYearRange,
	currentScenarioRange,
	refetchScenario,
	hasScenarioBeenUpdated,
}) => {
	const [scenarioIdFrom, setScenarioIdFrom] = useState<TValue>();
	const theme = useTheme() as ThemeType;
	const { keycloak } = useKeycloak();
	const queryClient = useQueryClient();

	const { mutate: changeRegionFamType } = useMutation(
		['change_region_family_type_demand'],
		(isEpv: boolean) =>
			axios.post('/demand_updates', {
				demand_scenario_id: scenarioId,
				sales_region: selectedItem!.item.sales_region,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
				...(isEpv ? { epv_family_id: '1' } : { horizon_family_id: '1' }),
			}),
		{
			onSuccess: ({ data }, isEpv) => {
				setScenarioData(data);
				queryClient.invalidateQueries(['demand_tree', scenarioId]);
				setSelectedItem((_) =>
					produce(_, (draft) => {
						draft!.item.fam_type = isEpv ? 'epv' : '';
					})
				);
			},
		}
	);

	const { mutate: copyUpdates } = useMutation(
		['copy_updates_demand'],
		async () => {
			axios.post(
				'/demand_scenario/copyupdates' +
					buildParams({
						scenario_id_from: scenarioIdFrom,
						scenario_id_to: scenarioId,
						sales_region: selectedItem!.item.sales_region,
						user: keycloak?.tokenParsed?.preferred_username,
					})
			);
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['demand_tree', scenarioId]);
				queryClient.invalidateQueries([
					'scenario',
					scenarioId,
					selectedItem?.value,
				]);
			},
		}
	);

	const { mutate: reset } = useMutation(
		['reset_demand_family'],
		(isSalesRegion: boolean) =>
			axios.post('/demand_updates', {
				demand_scenario_id: scenarioId,
				sales_region: selectedItem!.item.sales_region,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
				...(!isSalesRegion && {
					horizon_family_id: selectedItem!.item.family_code,
				}),
			}),
		{
			onSuccess: ({ data }) => {
				setScenarioData(data);
			},
		}
	);

	return selectedItem ? (
		<ScenarioConfig>
			{selectedItem.depth === 1 ? (
				<>
					<ScenarioConfigItem
						style={{
							flex: 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							gap: '1rem',
						}}>
						<h3
							style={{
								color: theme.background.primary,
							}}>
							Scenario type
						</h3>

						<div>Horizon 2 Family</div>
						<ToggleSwitch
							onToggle={async (checked) => {
								if (
									await confirmModal({
										content: `Are you sure you want to change the family type for the region : ${
											selectedItem!.item.sales_region
										} ? Family updates will be lost.`,
									})
								) {
									changeRegionFamType(checked);
									return checked;
								}
								return !checked;
							}}
							checked={selectedItem.item.fam_type === 'epv'}
						/>
						<div>EPV Family</div>

						<Button
							onClick={async () => {
								if (
									await confirmModal({
										content: `Are you sure you want to reset this region : "${
											selectedItem!.item.sales_region
										}" ?`,
									})
								) {
									reset(true);
								}
							}}>
							Reset to initial values
						</Button>
					</ScenarioConfigItem>
					<ScenarioConfigItem
						style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
						<Row center className='full'>
							<h3
								style={{
									color: theme.background.primary,
								}}>
								Hierarchy
							</h3>
							<Button
								disabled={!scenarioIdFrom}
								onClick={async () => {
									const message = `Are you sure you want to apply the updates to the scenario : "${
										scenarios?.find(
											(scenario) => scenario.value === scenarioIdFrom
										)?.label
									}"?`;
									if (
										await confirmModal({
											content: message,
										})
									) {
										copyUpdates();
									}
								}}>
								Apply to current scenario
							</Button>
							<SelectField
								options={
									scenarios?.filter(
										(scenario) => scenario.value !== scenarioId
									) ?? []
								}
								title='Load a scenario'
								value={scenarioIdFrom}
								setValue={setScenarioIdFrom}
							/>
						</Row>
					</ScenarioConfigItem>
				</>
			) : (
				<ScenarioConfigItem
					style={{
						flex: 1,
						display: 'flex',
						justifyContent: 'center',
						gap: '1rem',
					}}>
					<Button
						onClick={async () => {
							if (
								await confirmModal({
									content: `Are you sure you want to reset this family: "${
										selectedItem!.item.family_name
									}" ?`,
								})
							) {
								reset(false);
							}
						}}>
						Reset to initial values
					</Button>
					<Button
						disabled={hasScenarioBeenUpdated}
						onClick={async () => {
							if (
								await confirmModal({
									content: `Are you sure you want to reload the quantities ?`,
								})
							) {
								refetchScenario();
							}
						}}>
						Recompute quantities
					</Button>
				</ScenarioConfigItem>
			)}
			<ScenarioConfigItem
				style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
				<RangeField
					value={yearRange}
					setValue={setYearRange}
					min={min}
					max={max}
					setValueOnRelease
					blockMinAt={currentYear()}
					onRelease={(v) => {
						setYearRange(v);
						// refresh scenario if the current scenario's range in lower/greater than the new range
						if (
							currentScenarioRange &&
							isTupleInRange(v, currentScenarioRange)
						) {
							queryClient.invalidateQueries([
								'demand_scenario',
								scenarioId,
								selectedItem?.value,
							]);
						}
					}}
				/>
			</ScenarioConfigItem>
		</ScenarioConfig>
	) : null;
};

export default DemandConfig;
